import React, { useState } from "react";
import Input from "@/components/Input/Input";
import { Button, Modal } from "@mui/material";
import addImageIcon from "@/assets/icons/image-upload.svg";
import addTextIcon from "@/assets/icons/txt-upload.svg";
import styles from "./EditMusicModal.module.scss";
import { truncateDescription } from "@/helpers/truncateDescr";
import Textarea from "@/components/Textarea/Textarea";
import { IMedia } from "@/store/models/video.models";
import CustomCheckboxes from "@/components/CheckBoxes/CheckBoxes";

type Props = {
  isOpenEditModal: boolean;
  setIsOpenEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedMusic: IMedia | null;
  setSelectedMusic: React.Dispatch<React.SetStateAction<IMedia | null>>;
  handleEditMusic: (formData: FormData) => Promise<void>;
};

const options = [
  { value: "New on KidNation", label: "New on KidNation" },
  { value: "Only on KidNation", label: "Only on KidNation" },
];

const EditMusicModal = ({
  isOpenEditModal,
  setIsOpenEditModal,
  selectedMusic,
  setSelectedMusic,
  handleEditMusic,
}: Props) => {
  const [imgFile, setImgFile] = useState<string | null>(null);
  const [subtitleFileName, setSubtitleFileName] = useState<string>("");
  const [selectedSections, setSelectedSections] = useState<string[]>(
    selectedMusic?.sections ?? []
  );

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: string
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (fileType === "photo") {
        setImgFile(URL.createObjectURL(file));
        setSelectedMusic((prev) => ({
          ...prev!,
          thumbnailFile: file,
        }));
      } else if (fileType === "srt") {
        setSubtitleFileName(file.name);
        setSelectedMusic((prev) => ({
          ...prev!,
          subtitleFile: file,
        }));
      }
    }
  };

  const handleSave = () => {
    if (selectedMusic) {
      const formData = new FormData();
      formData.append("uid", selectedMusic.uid);
      formData.append("title", selectedMusic.title || "");
      formData.append("description", selectedMusic.description || "");
      if (selectedMusic.thumbnailFile) {
        formData.append("thumbnailFile", selectedMusic.thumbnailFile);
      }
      if (selectedMusic.url) {
        formData.append("url", selectedMusic.url);
      }
      if (selectedMusic.subtitleFile) {
        formData.append("subtitleFile", selectedMusic.subtitleFile);
      }
      if (selectedSections && selectedSections.length > 0) {
        selectedSections.forEach((section) => {
          formData.append("sections", section);
        });
      }

      handleEditMusic(formData);
    }
  };

  return (
    <Modal
      aria-labelledby="edit audio"
      aria-describedby="for editing audio Enter the required data and click save"
      open={isOpenEditModal}
      onClose={() => setIsOpenEditModal(false)}
    >
      <div className={styles.modalContainer}>
        <h3 className={styles.modalTitle}>Edit audio</h3>
        <div className={styles.modalContent}>
          <Input
            label="Name"
            value={selectedMusic?.title}
            onChange={(e) =>
              setSelectedMusic((prev) => ({
                ...prev!,
                title: e.target.value,
              }))
            }
          />

          <CustomCheckboxes
            label="Select Sections"
            options={options}
            value={selectedSections}
            onChange={setSelectedSections}
          />

          <Textarea
            label="Description"
            value={selectedMusic?.description || ""}
            onChange={(e) =>
              setSelectedMusic((prev) => ({
                ...prev!,
                description: e.target.value,
              }))
            }
          />

          <label
            className={styles.modalAddItem}
            htmlFor="thumbnail"
            id="dropcontainer"
          >
            <img className={styles.modalImg} src={addImageIcon} alt="img" />
            <input
              id="thumbnail"
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, "photo")}
            />

            <span className={styles.modalPrimaryText}>
              or drag and drop the thumbnail here (*.png)
            </span>
          </label>

          {imgFile && (
            <img
              className={styles.thumbnailPreview}
              src={imgFile}
              alt="Thumbnail Preview"
              width={100}
              height={100}
            />
          )}

          {/* This functionality is not working yet */}

          {/* <p className={styles.modalHelper}>or add a link to the file</p>
          <Input
            label=""
            value=""
            onChange={(e) =>
              setSelectedMusic((prev) => ({
                ...prev!,
                thumbnail: e.target.value,
              }))
            }
            placeholder="Enter the link"
          /> */}

          <label
            className={styles.modalAddItem}
            htmlFor="subtitles"
            id="dropcontainer"
          >
            <img className={styles.modalImg} src={addTextIcon} alt="img" />
            <input
              id="subtitles"
              type="file"
              accept=".srt"
              onChange={(e) => handleFileUpload(e, "srt")}
            />

            <span className={styles.modalPrimaryText}>
              {subtitleFileName
                ? truncateDescription(subtitleFileName, 15)
                : "or drag and drop the file here (*.srt)"}
            </span>
          </label>

          {/* This functionality is not working yet */}
          {/* <p className={styles.modalHelper}>or add a link to the file</p>
          <Input
            label=""
            value={(selectedMusic?.subtitleUrl as string) || ""}
            onChange={(e) =>
              setSelectedMusic((prev) => ({
                ...prev!,
                subtitleUrl: e.target.value,
              }))
            }
            placeholder="Enter the link"
          /> */}
          <div className={styles.btns}>
            <Button
              sx={{
                textTransform: "capitalize",
                height: "44px",
                width: "130px",
                backgroundColor: "#008000",
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#008000",
                },
              }}
              variant="contained"
              onClick={handleSave}
            >
              Save
            </Button>

            <Button
              variant="outlined"
              sx={{
                textTransform: "capitalize",
                height: "44px",
                width: "130px",
                border: "1px solid #5570F1",
                color: "#5570F1",
                borderRadius: "12px",
              }}
              onClick={() => setIsOpenEditModal(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditMusicModal;
